import React, { useState, useRef, useEffect } from "react";
import ChevronRightTwoToneIcon from "@mui/icons-material/ChevronRightTwoTone";
import { useLocation } from "react-router-dom";
import { useNavigate } from "../../Navigate";
import {
  useAppUserManager,
  useClusterManager,
  useDistrictManager,
  useOrgManager,
  useSchoolManager,
} from "../../../hooks/useManagers";
import "./sidebarMenu.scss";
import { useCallback } from "react";
import { Input } from "rsuite";

// Elements that can receive keyboard focus within a submenu group.
// Leaf items get tabIndex={-1} when their submenu is collapsed, so this
// selector must exclude those explicitly (a plain <button>/etc. with no
// tabindex attribute at all is still tabbable by default and must match).
const SUBMENU_FOCUSABLE_SELECTOR =
  'button:not([tabindex="-1"]), [href]:not([tabindex="-1"]), input:not([tabindex="-1"]), select:not([tabindex="-1"]), textarea:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';

// Shared keyboard-navigation handler for the sidebar's collapsible submenu
// groups. Handles Escape (collapse + return focus to the toggle) and
// ArrowUp/ArrowDown (move focus between the toggle button and its submenu
// items) without trapping Tab focus.
const handleSubmenuKeyDown = (event, { toggleRef, containerRef, setOpen }) => {
  if (event.key === "Escape") {
    event.stopPropagation();
    setOpen(false);
    if (toggleRef?.current) {
      toggleRef.current.focus();
    }
    return;
  }
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    const container = containerRef?.current;
    const toggleEl = toggleRef?.current;
    if (!container || !toggleEl) {
      return;
    }
    const items = [
      toggleEl,
      ...Array.from(container.querySelectorAll(SUBMENU_FOCUSABLE_SELECTOR)),
    ];
    const currentIndex = items.indexOf(event.target);
    if (currentIndex === -1) {
      return;
    }
    event.preventDefault();
    const nextIndex =
      event.key === "ArrowDown"
        ? Math.min(currentIndex + 1, items.length - 1)
        : Math.max(currentIndex - 1, 0);
    items[nextIndex]?.focus();
  }
};

const SidebarMenu = ({ onSidebarToggle }) => {
  const menuOne = useRef();
  const menuTwo = useRef();
  const menuThree = useRef();
  const menuFour = useRef();
  const menuFive = useRef();
  const menuSix = useRef();
  const menuSeven = useRef();
  const menuEight = useRef();
  const contentLibraryToggleRef = useRef();
  const certificationToggleRef = useRef();
  const observationToggleRef = useRef();
  const surveyToggleRef = useRef();
  const reportsToggleRef = useRef();
  const adminToggleRef = useRef();
  const devToggleRef = useRef();
  const dataDashboardToggleRef = useRef();
  const authMgr = useAppUserManager();
  const permissions = authMgr?.GetGlobalState()?.permissions ?? [];
  const userProfile = authMgr?.GetGlobalState()?.userProfile;
  const observationConfig = authMgr?.GetGlobalState()?.observationConfig;
  const navigate = useNavigate();

  // default open/close state of the menus inside the sidebar
  const [activeMenu, setActiveMenu] = useState(null);
  const [contentLibraryMenu, setContentLibraryMenu] = useState(false);
  const [observationMenu, setObservationMenu] = useState(false);
  const [surveyMenu, setSurveyMenu] = useState(false);
  const [dataDashboardMenu, setDataDashboardMenu] = useState(false);
  const [certificationMenu, setCertificationMenu] = useState(false);
  const [adminMenu, setAdminMenu] = useState(false);
  const [devMenu, setDevMenu] = useState(false);
  const [reportsMenu, setReportsMenu] = useState(false);
  const [contentLibraryHeight, setContentLibaryHeight] = useState(999);
  const [surveyHeight, setSurveyHeight] = useState(999);
  const [observationHeight, setObservationHeight] = useState(999);
  const [certificationHeight, setCertificationHeight] = useState(999);
  const [reportsHeight, setReportsHeight] = useState(999);
  const [adminHeight, setAdminHeight] = useState(999);
  const [devHeight, setDevHeight] = useState(999);
  const [dataDashboardHeight, setDataDashboardHeight] = useState(999);

  const orgMgr = useOrgManager();
  const districtMgr = useDistrictManager();
  const schoolMgr = useSchoolManager();
  const clusterMgr = useClusterManager();
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname?.toLowerCase().includes("/admin")) {
      setAdminMenu(true);
      setContentLibraryMenu(false);
      setCertificationMenu(false);
      setObservationMenu(false);
      setDevMenu(false);
      setSurveyMenu(false);
      setReportsMenu(false);
      setDataDashboardMenu(false);
    } else if (pathname?.toLowerCase().includes("/observation")) {
      setAdminMenu(false);
      setContentLibraryMenu(false);
      setCertificationMenu(false);
      setObservationMenu(true);
      setDevMenu(false);
      setSurveyMenu(false);
      setReportsMenu(false);
      setDataDashboardMenu(false);
    } else if (pathname?.toLowerCase().includes("/survey/datadashboardsurvey")) {
      setAdminMenu(false);
      setContentLibraryMenu(false);
      setObservationMenu(false);
      setDevMenu(false);
      setSurveyMenu(false);
      setReportsMenu(false);
      setDataDashboardMenu(true);
    } else if (pathname?.toLowerCase().includes("/survey")) {
      setAdminMenu(false);
      setContentLibraryMenu(false);
      setObservationMenu(false);
      setDevMenu(false);
      setSurveyMenu(true);
      setReportsMenu(false);
      setDataDashboardMenu(false);
    } else if (pathname?.toLowerCase().includes("/certifications")) {
      setAdminMenu(false);
      setContentLibraryMenu(false);
      setCertificationMenu(true);
      setObservationMenu(false);
      setDevMenu(false);
      setSurveyMenu(false);
      setReportsMenu(false);
      setDataDashboardMenu(false);
    } else if (pathname?.toLowerCase().includes("/dev")) {
      setAdminMenu(false);
      setContentLibraryMenu(false);
      setCertificationMenu(true);
      setObservationMenu(false);
      setDevMenu(true);
      setSurveyMenu(false);
      setReportsMenu(false);
      setDataDashboardMenu(false);
    } else if (pathname?.toLowerCase().includes("/data-dashboard/reports")) {
      setAdminMenu(false);
      setContentLibraryMenu(false);
      setCertificationMenu(false);
      setObservationMenu(false);
      setDevMenu(false);
      setSurveyMenu(false);
      setReportsMenu(false);
      setDataDashboardMenu(true);
    } else if (pathname?.toLowerCase().includes("/reports")) {
      setAdminMenu(false);
      setContentLibraryMenu(false);
      setCertificationMenu(true);
      setObservationMenu(false);
      setDevMenu(false);
      setSurveyMenu(false);
      setReportsMenu(true);
      setDataDashboardMenu(false);
    } else {
      setContentLibraryMenu(true);
      setDataDashboardMenu(false);
    }
  }, [pathname]);

  useEffect(() => {
    if (
      pathname.toLowerCase().includes("/organization") ||
      pathname.toLowerCase().includes("/district") ||
      pathname.toLowerCase().includes("/school") ||
      pathname.toLowerCase().includes("/cluster")
    ) {
      orgMgr?.filterData({});
      districtMgr?.filterData({});
      schoolMgr?.filterData({});
      clusterMgr?.filterData({});
    }
  }, [orgMgr, districtMgr, schoolMgr, clusterMgr, pathname]);

  useEffect(() => {
    handleResize();
  }, [
    menuOne.current,
    menuTwo.current,
    menuThree.current,
    menuFour.current,
    menuFive.current,
    menuSeven.current,
    menuEight.current,
  ]);

  const handleSidebarToggle = (path) => (e) => {
    navigate(path);
  };

  const handleResize = () => {
    if (menuOne.current) {
      setContentLibaryHeight(menuOne.current.scrollHeight);
    }
    if (menuTwo.current) {
      setCertificationHeight(menuTwo.current.scrollHeight);
    }
    if (menuThree.current) {
      setObservationHeight(menuThree.current.scrollHeight);
    }
    if (menuFour.current) {
      setSurveyHeight(menuFour.current.scrollHeight);
    }
    if (menuFive.current) {
      setReportsHeight(menuFive.current.scrollHeight);
    }
    if (menuSix.current) {
      setAdminHeight(menuSix.current.scrollHeight);
    }
    if (menuSeven.current) {
      setDevHeight(menuSeven.current.scrollHeight);
    }
    if (menuEight.current) {
      setDataDashboardHeight(menuEight.current.scrollHeight);
    }
  };

  const getOptionClass = (optionName, defaultClass) => {
    const currentPath = pathname.toLowerCase().replace(/\/+$/, "") || "/";
    const optionPath = optionName.toLowerCase().replace(/\/+$/, "") || "/";
    const isActive = currentPath === optionPath ||
      (optionPath !== "/" && currentPath.startsWith(`${optionPath}/`));
    return isActive ? `${defaultClass} active` : defaultClass;
  };

  return (
    <>
      <div className="sidebar-navigation">
        <h1
          className={getOptionClass("/", "nav-link")}
          role="button"
          tabIndex={0}
          onClick={() => {
            navigate("/");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate("/");
            }
          }}
        >
          Home
        </h1>
        {authMgr?.AccessCheck([permissions?.MyDashboard]) && (
          <h1
            className={getOptionClass("/my-dashboard", "nav-link")}
            role="button"
            tabIndex={0}
            onClick={() => {
              navigate("/my-dashboard");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate("/my-dashboard");
              }
            }}
          >
            Dashboard
          </h1>
        )}
        {authMgr?.AccessCheck([
          permissions?.Administration,
          permissions?.Subscription,
          permissions?.Transaction,
          permissions?.NavApplicationAdminMgmt,
        ]) && (
          <h1
            className={getOptionClass("/admin/Payment", "nav-link")}
            role="button"
            tabIndex={0}
            onClick={() => navigate("/admin/Payment")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate("/admin/Payment");
              }
            }}
          >
            Subscription Examples
          </h1>
        )}
        {authMgr?.AccessCheck([permissions?.NavAnalytics]) && (
          <h1
            className={getOptionClass("/analytics", "nav-link")}
            role="button"
            tabIndex={0}
            onClick={() => navigate("/analytics")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate("/analytics");
              }
            }}
          >
            Analytics
          </h1>
        )}
        {authMgr?.AccessCheck([permissions?.VirtualLibrarianUser]) && (
          <h1
            className={getOptionClass("/EchoAI", "nav-link")}
            role="button"
            tabIndex={0}
            onClick={() => navigate("/EchoAI")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate("/EchoAI");
              }
            }}
          >
            Enhanced Search
          </h1>
        )}
        {permissions && authMgr && (
          <ul>
            {authMgr.AccessCheck([permissions?.ContentLibrary]) && (
              <>
                <li>
                  <button
                    type="button"
                    ref={contentLibraryToggleRef}
                    aria-expanded={contentLibraryMenu}
                    onClick={() => {
                      setContentLibraryMenu(!contentLibraryMenu);
                      setActiveMenu("contentlibrary");
                    }}
                    onKeyDown={(e) =>
                      handleSubmenuKeyDown(e, {
                        toggleRef: contentLibraryToggleRef,
                        containerRef: menuOne,
                        setOpen: setContentLibraryMenu,
                      })
                    }
                    className="groupOption"
                  >
                    <span className="menuOptions hasHover nav-link">
                      Content Library
                    </span>
                    <span
                      aria-hidden="true"
                      className={
                        contentLibraryMenu
                          ? "sidebar-icon-indicator"
                          : "sidebar-icon-indicator active"
                      }
                    >
                      <ChevronRightTwoToneIcon />
                    </span>
                  </button>
                </li>
                <li
                  ref={menuOne}
                  onKeyDown={(e) =>
                    handleSubmenuKeyDown(e, {
                      toggleRef: contentLibraryToggleRef,
                      containerRef: menuOne,
                      setOpen: setContentLibraryMenu,
                    })
                  }
                  style={{
                    maxHeight: contentLibraryMenu
                      ? `${contentLibraryHeight}px`
                      : "0px",
                  }}
                >
                  {authMgr?.AccessCheck([
                    permissions?.SearchContent,
                    permissions?.ContentLibrary,
                  ]) && (
                    <button type="button"
                      tabIndex={contentLibraryMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/content")}
                      className={
                        "/content" == pathname.toLowerCase()
                          ? "groupSubOption active"
                          : "groupSubOption"
                      }
                    >
                      <span className="menuSubOptions">Search Content</span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.SearchShareContent,
                    permissions?.ContentLibrary,
                  ]) && (
                    <button type="button"
                      tabIndex={contentLibraryMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/share")}
                      className={getOptionClass("/share", "groupSubOption")}
                    >
                      <span className="menuSubOptions">Shared Content</span>
                    </button>
                  )}
                </li>
              </>
            )}
            {
              //add perms bakc
              ((authMgr?.AccessCheck([
                permissions?.Certification,
                permissions?.Certifications,
                permissions?.NavTeacherCertification,
              ]) &&
                observationConfig.CertificationUserPositionsTeacher.includes(
                  userProfile.UserPositionType
                )) ||
                (authMgr?.AccessCheck([
                  permissions?.Certification,
                  permissions?.Certifications,
                  permissions?.NavAdminCertification,
                ]) &&
                  observationConfig.CertificationUserPositionsAdmin.includes(
                    userProfile.UserPositionType
                  ))) && (
                <>
                  <li>
                    <button
                      type="button"
                      ref={certificationToggleRef}
                      aria-expanded={certificationMenu}
                      onClick={() => setCertificationMenu(!certificationMenu)}
                      onKeyDown={(e) =>
                        handleSubmenuKeyDown(e, {
                          toggleRef: certificationToggleRef,
                          containerRef: menuTwo,
                          setOpen: setCertificationMenu,
                        })
                      }
                      className="groupOption"
                    >
                      <span className="menuOptions hasHover nav-link">
                        Certifications
                      </span>
                      <span
                        aria-hidden="true"
                        className={
                          certificationMenu
                            ? "sidebar-icon-indicator"
                            : "sidebar-icon-indicator active"
                        }
                      >
                        <ChevronRightTwoToneIcon />
                      </span>
                    </button>
                  </li>

                  <li
                    ref={menuTwo}
                    onKeyDown={(e) =>
                      handleSubmenuKeyDown(e, {
                        toggleRef: certificationToggleRef,
                        containerRef: menuTwo,
                        setOpen: setCertificationMenu,
                      })
                    }
                    style={{
                      maxHeight: certificationMenu
                        ? `${certificationHeight}px`
                        : "0px",
                    }}
                  >
                    {((authMgr?.AccessCheck([
                      permissions?.Certification,
                      permissions?.NavTeacherCertification,
                      permissions?.Certifications,
                    ]) &&
                      observationConfig.CertificationUserPositionsTeacher.includes(
                        userProfile.UserPositionType
                      )) ||
                      authMgr?.AccessCheck([
                        permissions?.NavApplicationAdminMgmt,
                        permissions?.Certification,
                        permissions?.NavTeacherCertification,
                        permissions?.Certifications,
                      ])) && (
                      //Add a check for permissions to either return no userProfileId if you're an admin.
                      <button type="button"
                        tabIndex={certificationMenu ? 0 : -1}
                        onClick={handleSidebarToggle(
                          `/certifications/teacher/`
                        )}
                        className={getOptionClass(
                          "/certifications/teacher",
                          "groupSubOption"
                        )}
                      >
                        <span className="menuSubOptions">
                          Teacher Evaluator
                        </span>
                      </button>
                    )}
                    {((authMgr?.AccessCheck([
                      permissions?.Certification,
                      permissions?.NavAdminCertification,
                      permissions?.Certifications,
                    ]) &&
                      observationConfig.CertificationUserPositionsAdmin.includes(
                        userProfile.UserPositionType
                      )) ||
                      authMgr?.AccessCheck([
                        permissions?.NavApplicationAdminMgmt,
                        permissions?.Certification,
                        permissions?.NavAdminCertification,
                        permissions?.Certifications,
                      ])) && (
                      <button type="button"
                        tabIndex={certificationMenu ? 0 : -1}
                        onClick={handleSidebarToggle(
                          `/certifications/administrator/`
                        )}
                        className={getOptionClass(
                          "/certifications/administrator",
                          "groupSubOption"
                        )}
                      >
                        <span className="menuSubOptions">
                          Administrator Evaluator
                        </span>
                      </button>
                    )}
                  </li>
                </>
              )
            }

            {(authMgr?.AccessCheck([
              permissions?.Observation,
              permissions?.Survey,
              permissions?.NavPrincipalObservation,
            ]) ||
              authMgr?.AccessCheck([
                permissions?.Observation,
                permissions?.Survey,
                permissions?.NavTeacherObservation,
              ])) && (
              <>
                <li>
                  <button
                    type="button"
                    ref={observationToggleRef}
                    aria-expanded={observationMenu}
                    onClick={() => setObservationMenu(!observationMenu)}
                    onKeyDown={(e) =>
                      handleSubmenuKeyDown(e, {
                        toggleRef: observationToggleRef,
                        containerRef: menuThree,
                        setOpen: setObservationMenu,
                      })
                    }
                    className="groupOption"
                  >
                    <span className="menuOptions hasHover">
                      Observations
                    </span>
                    <span
                      aria-hidden="true"
                      className={
                        observationMenu
                          ? "sidebar-icon-indicator"
                          : "sidebar-icon-indicator active"
                      }
                    >
                      <ChevronRightTwoToneIcon />
                    </span>
                  </button>
                </li>
                <li
                  ref={menuThree}
                  onKeyDown={(e) =>
                    handleSubmenuKeyDown(e, {
                      toggleRef: observationToggleRef,
                      containerRef: menuThree,
                      setOpen: setObservationMenu,
                    })
                  }
                  style={{
                    maxHeight: observationMenu
                      ? `${observationHeight}px`
                      : "0px",
                  }}
                >
                  {authMgr?.AccessCheck([
                    permissions?.Observation,
                    permissions?.Survey,
                    permissions?.NavTeacherObservation,
                  ]) && (
                    <button type="button"
                      tabIndex={observationMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/Observation/teacher")}
                      className={getOptionClass(
                        "/observation/teacher",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">
                        Teacher Observations
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Observation,
                    permissions?.Survey,
                    permissions?.NavPrincipalObservation,
                  ]) && (
                    <button type="button"
                      tabIndex={observationMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/Observation/SchoolAdmin")}
                      className={getOptionClass(
                        "/observation/SchoolAdmin",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">
                        School Admin Observations
                      </span>
                    </button>
                  )}
                </li>
              </>
            )}

            {(authMgr?.AccessCheck([
              permissions?.Survey,
              permissions?.NavGeneralSurvey,
            ]) ||
              authMgr?.AccessCheck([
                permissions?.Survey,
                permissions?.NavAdministratorSurvey,
              ]) ||
              authMgr?.AccessCheck([
                permissions?.Survey,
                permissions?.NavTeacherSurvey,
              ])) && (
              <>
                <li>
                  <button
                    type="button"
                    ref={surveyToggleRef}
                    aria-expanded={surveyMenu}
                    onClick={() => setSurveyMenu(!surveyMenu)}
                    onKeyDown={(e) =>
                      handleSubmenuKeyDown(e, {
                        toggleRef: surveyToggleRef,
                        containerRef: menuFour,
                        setOpen: setSurveyMenu,
                      })
                    }
                    className="groupOption"
                  >
                    <span className="menuOptions hasHover">Surveys</span>
                    <span
                      aria-hidden="true"
                      className={
                        surveyMenu
                          ? "sidebar-icon-indicator"
                          : "sidebar-icon-indicator active"
                      }
                    >
                      <ChevronRightTwoToneIcon />
                    </span>
                  </button>
                </li>
                <li
                  ref={menuFour}
                  onKeyDown={(e) =>
                    handleSubmenuKeyDown(e, {
                      toggleRef: surveyToggleRef,
                      containerRef: menuFour,
                      setOpen: setSurveyMenu,
                    })
                  }
                  style={{
                    maxHeight: surveyMenu ? `${surveyHeight}px` : "0px",
                  }}
                >
                  {authMgr?.AppUserState?.HasGeneralSurvey &&
                    authMgr?.AccessCheck([
                      permissions?.GeneralSurvey,
                      permissions?.Survey,
                      permissions?.NavGeneralSurvey,
                    ]) && (
                      <button type="button"
                        tabIndex={surveyMenu ? 0 : -1}
                        onClick={handleSidebarToggle("/survey/generalsurvey")}
                        className={getOptionClass(
                          "/survey/generalsurvey",
                          "groupSubOption"
                        )}
                      >
                        <span className="menuSubOptions">General Surveys</span>
                      </button>
                    )}
                  {authMgr?.AccessCheck([
                    permissions?.Survey,
                    permissions?.NavTeacherSurvey,
                  ]) && (
                    <button type="button"
                      tabIndex={surveyMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/survey/teachersurvey")}
                      className={getOptionClass(
                        "/survey/teachersurvey",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">Teacher Surveys</span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Survey,
                    permissions?.NavAdministratorSurvey,
                  ]) && (
                    <button type="button"
                      tabIndex={surveyMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/survey/principalsurvey")}
                      className={getOptionClass(
                        "/survey/principalsurvey",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">
                        School Admin Surveys
                      </span>
                    </button>
                  )}
                </li>
              </>
            )}

            {authMgr?.AccessCheck([
              permissions?.TDOEDataDashboardSurveyEntry,
            ]) && (
              <>
                <li>
                  <button
                    type="button"
                    ref={dataDashboardToggleRef}
                    aria-expanded={dataDashboardMenu}
                    onClick={() => setDataDashboardMenu(!dataDashboardMenu)}
                    onKeyDown={(e) =>
                      handleSubmenuKeyDown(e, {
                        toggleRef: dataDashboardToggleRef,
                        containerRef: menuEight,
                        setOpen: setDataDashboardMenu,
                      })
                    }
                    className="groupOption"
                  >
                    <span className="menuOptions hasHover">
                      Data Dashboard
                    </span>
                    <span
                      aria-hidden="true"
                      className={
                        dataDashboardMenu
                          ? "sidebar-icon-indicator"
                          : "sidebar-icon-indicator active"
                      }
                    >
                      <ChevronRightTwoToneIcon />
                    </span>
                  </button>
                </li>
                <li
                  ref={menuEight}
                  onKeyDown={(e) =>
                    handleSubmenuKeyDown(e, {
                      toggleRef: dataDashboardToggleRef,
                      containerRef: menuEight,
                      setOpen: setDataDashboardMenu,
                    })
                  }
                  style={{
                    maxHeight: dataDashboardMenu
                      ? `${dataDashboardHeight}px`
                      : "0px",
                  }}
                >
                  {authMgr?.AccessCheck([
                    permissions?.TDOEDataDashboardSurveyEntry,
                  ]) && (
                    <button type="button"
                      tabIndex={dataDashboardMenu ? 0 : -1}
                      onClick={handleSidebarToggle(
                        "/survey/datadashboardsurvey"
                      )}
                      className={getOptionClass(
                        "/survey/datadashboardsurvey",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">Surveys</span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.TDOEDataDashboardReporting,
                  ]) && (
                    <button type="button"
                      tabIndex={dataDashboardMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/data-dashboard/reports")}
                      className={getOptionClass(
                        "/data-dashboard/reports",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">Reports</span>
                    </button>
                  )}
                </li>
              </>
            )}

            {(authMgr?.AccessCheck([permissions?.NavTeacherReports]) ||
              authMgr?.AccessCheck([permissions?.NavAdminReports]) ||
              authMgr?.AccessCheck([permissions?.NavNIETReports])) && (
              <>
                <li>
                  <button
                    type="button"
                    ref={reportsToggleRef}
                    aria-expanded={reportsMenu}
                    onClick={() => setReportsMenu(!reportsMenu)}
                    onKeyDown={(e) =>
                      handleSubmenuKeyDown(e, {
                        toggleRef: reportsToggleRef,
                        containerRef: menuFive,
                        setOpen: setReportsMenu,
                      })
                    }
                    className="groupOption"
                  >
                    <span className="menuOptions hasHover">Reports</span>
                    <span
                      aria-hidden="true"
                      className={
                        reportsMenu
                          ? "sidebar-icon-indicator"
                          : "sidebar-icon-indicator active"
                      }
                    >
                      <ChevronRightTwoToneIcon />
                    </span>
                  </button>
                </li>
                <li
                  ref={menuFive}
                  onKeyDown={(e) =>
                    handleSubmenuKeyDown(e, {
                      toggleRef: reportsToggleRef,
                      containerRef: menuFive,
                      setOpen: setReportsMenu,
                    })
                  }
                  style={{
                    maxHeight: reportsMenu ? `${reportsHeight}px` : "0px",
                  }}
                >
                  {authMgr?.AccessCheck([permissions?.NavTeacherReports]) && (
                    <button type="button"
                      tabIndex={reportsMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/reports/teacher")}
                      className={getOptionClass(
                        "/reports/teacher",
                        "groupSubOption"
                      )}
                    >
                      <span className={"menuSubOptions"}>Teacher Reports</span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([permissions?.NavAdminReports]) && (
                    <button type="button"
                      tabIndex={reportsMenu ? 0 : -1}
                      onClick={handleSidebarToggle(
                        "/reports/school-administrator"
                      )}
                      className={getOptionClass(
                        "/reports/school-administrator",
                        "groupSubOption"
                      )}
                    >
                      <span className={"menuSubOptions"}>
                        School Admin Reports
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([permissions?.NavNIETReports]) && (
                    <button type="button"
                      tabIndex={reportsMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/reports/niet")}
                      className={getOptionClass(
                        "/reports/niet",
                        "groupSubOption"
                      )}
                    >
                      <span className={"menuSubOptions"}>NIET Reports</span>
                    </button>
                  )}
                </li>
              </>
            )}
            {authMgr?.AccessCheck([permissions?.Administration]) && (
              <>
                <li>
                  <button
                    type="button"
                    ref={adminToggleRef}
                    aria-expanded={adminMenu}
                    onClick={() => setAdminMenu(!adminMenu)}
                    onKeyDown={(e) =>
                      handleSubmenuKeyDown(e, {
                        toggleRef: adminToggleRef,
                        containerRef: menuSix,
                        setOpen: setAdminMenu,
                      })
                    }
                    className="groupOption"
                  >
                    <span className="menuOptions hasHover">
                      Administration
                    </span>
                    <span
                      aria-hidden="true"
                      className={
                        adminMenu
                          ? "sidebar-icon-indicator"
                          : "sidebar-icon-indicator active"
                      }
                    >
                      <ChevronRightTwoToneIcon />
                    </span>
                  </button>
                </li>
                <li
                  ref={menuSix}
                  onKeyDown={(e) =>
                    handleSubmenuKeyDown(e, {
                      toggleRef: adminToggleRef,
                      containerRef: menuSix,
                      setOpen: setAdminMenu,
                    })
                  }
                  style={{ maxHeight: adminMenu ? `${adminHeight}px` : "0px" }}
                >
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.Content,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/content")}
                      className={getOptionClass(
                        "/admin/content",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">Content Management</span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.FolderSyncFile,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/contentSync")}
                      className={getOptionClass(
                        "/admin/contentSync",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions sub">
                        Content Sync Management
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.Tag,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/tag")}
                      className={getOptionClass("/admin/tag", "groupSubOption")}
                    >
                      <span className="menuSubOptions sub">Tag Management</span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.Client,
                    permissions?.NavOrganizationMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/Organization")}
                      className={getOptionClass(
                        "/admin/organization",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">
                        Organization Management
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.District,
                    permissions?.NavDistrictMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/District")}
                      className={getOptionClass(
                        "/admin/district",
                        "groupSubOption"
                      )}
                    >
                      <span
                        className={`menuSubOptions ${
                          !authMgr?.AccessCheck([
                            permissions?.NavOrganizationMgmt,
                          ])
                            ? ""
                            : "sub"
                        }`}
                      >
                        District Management
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.School,
                    permissions?.NavSchoolMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/School")}
                      className={getOptionClass(
                        "/admin/school",
                        "groupSubOption"
                      )}
                    >
                      <span
                        className={`menuSubOptions ${
                          !authMgr?.AccessCheck([
                            permissions?.NavDistrictMgmt,
                          ]) &&
                          !authMgr?.AccessCheck([
                            permissions?.NavOrganizationMgmt,
                          ])
                            ? ""
                            : "sub"
                        }`}
                      >
                        School Management
                      </span>
                    </button>
                  )}

                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.Cluster,
                    permissions?.NavClusterMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/Cluster")}
                      className={getOptionClass(
                        "/admin/cluster",
                        "groupSubOption"
                      )}
                    >
                      <span
                        className={`menuSubOptions  ${
                          !authMgr?.AccessCheck([permissions?.NavSchoolMgmt]) &&
                          !authMgr?.AccessCheck([
                            permissions?.NavOrganizationMgmt,
                          ]) &&
                          !authMgr?.AccessCheck([permissions?.NavDistrictMgmt])
                            ? ""
                            : "sub"
                        }`}
                      >
                        Cluster Management
                      </span>
                    </button>
                  )}

                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.UserProfile,
                    permissions?.NavUserMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/User")}
                      className={getOptionClass(
                        "/admin/user",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">User Management</span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.UserProfile,
                    permissions?.NavUserMgmt,
                    permissions?.AzureB2CSaveUserProfiles,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/StagedUser")}
                      className={getOptionClass(
                        "/admin/StagedUser",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions sub">
                        Staged User Management
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.Role,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/Role")}
                      className={getOptionClass(
                        "/admin/role",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions sub">
                        Role Management
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.Permission,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/Permission")}
                      className={getOptionClass(
                        "/admin/permission",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions sub">
                        Permission Management
                      </span>
                    </button>
                  )}

                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.SchoolYear,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/SchoolYear")}
                      className={getOptionClass(
                        "/admin/schoolyear",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">
                        School Year Management
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.Rubric,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/Rubric")}
                      className={getOptionClass(
                        "/admin/rubric",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">Rubric Management</span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.SurveyTemplate,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/SurveyTemplate")}
                      className={getOptionClass(
                        "/admin/surveytemplate",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">Survey Builder</span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.GeneralSurvey,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/generalSurvey")}
                      className={getOptionClass(
                        "/admin/generalSurvey",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions sub">
                        General Survey Management
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.FileImportExport,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/ImportExport")}
                      className={getOptionClass(
                        "/admin/ImportExport",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">
                        Import/Export Management
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.Notification,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/notification")}
                      className={getOptionClass(
                        "/admin/notification",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">
                        Notification Management
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.ObservationConfig,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/Configuration")}
                      className={getOptionClass(
                        "/admin/Configuration",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">
                        Configuration Management
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.ObservationConfig,
                    permissions?.Administration,
                    permissions?.TOS,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/TOS")}
                      className={getOptionClass("/admin/tos", "groupSubOption")}
                    >
                      <span className="menuSubOptions sub">
                        Terms of Service Management
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.ObservationConfig,
                    permissions?.Administration,
                    permissions?.NavApplicationAdminMgmt,
                    permissions?.NavCertMgmt,
                    permissions?.CertificationQuestion,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle(
                        "/admin/CertificationQuestion/teacher"
                      )}
                      className={getOptionClass(
                        "/admin/certificationquestion/teacher",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions  sub">
                        Teacher Eval Question Bank{" "}
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.ObservationConfig,
                    permissions?.Administration,
                    permissions?.NavApplicationAdminMgmt,
                    permissions?.NavCertMgmt,
                    permissions?.CertificationQuestion,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle(
                        "/admin/CertificationQuestion/schooladmin"
                      )}
                      className={getOptionClass(
                        "/admin/certificationquestion/schooladmin",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions  sub">
                        Admin Eval Question Bank
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.ObservationConfig,
                    permissions?.Administration,
                    permissions?.Transaction,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/Transaction")}
                      className={getOptionClass(
                        "/admin/transaction",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">
                        Transaction Management
                      </span>
                    </button>
                  )}

                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.PayoutTeacher,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/Payout/Teacher")}
                      className={getOptionClass(
                        "/admin/payout/teacher",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">Teacher Payout</span>
                    </button>
                  )}

                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.PayoutAdmin,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/Payout/Admin")}
                      className={getOptionClass(
                        "/admin/payout/admin",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">Admin Payout</span>
                    </button>
                  )}

                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.Feedback,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/Feedback")}
                      className={getOptionClass(
                        "/admin/feedback",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">
                        Feedback Management
                      </span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.Log,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/Log")}
                      className={getOptionClass("/admin/log", "groupSubOption")}
                    >
                      <span className="menuSubOptions">Log Management</span>
                    </button>
                  )}
                  {authMgr?.AccessCheck([
                    permissions?.Administration,
                    permissions?.VirtualLibrarianMgmt,
                    permissions?.NavApplicationAdminMgmt,
                  ]) && (
                    <button type="button"
                      tabIndex={adminMenu ? 0 : -1}
                      onClick={handleSidebarToggle("/admin/EchoAI")}
                      className={getOptionClass(
                        "/admin/EchoAI",
                        "groupSubOption"
                      )}
                    >
                      <span className="menuSubOptions">Enhanced Search Management</span>
                    </button>
                  )}
                </li>
                {authMgr?.AccessCheck([permissions?.Development]) && (
                  <>
                    <li>
                      <button
                        type="button"
                        ref={devToggleRef}
                        aria-expanded={devMenu}
                        onClick={() => setDevMenu(!devMenu)}
                        onKeyDown={(e) =>
                          handleSubmenuKeyDown(e, {
                            toggleRef: devToggleRef,
                            containerRef: menuSeven,
                            setOpen: setDevMenu,
                          })
                        }
                        className="groupOption"
                      >
                        <span className="menuOptions hasHover">
                          Development
                        </span>
                        <span
                          aria-hidden="true"
                          className={
                            devMenu
                              ? "sidebar-icon-indicator"
                              : "sidebar-icon-indicator active"
                          }
                        >
                          <ChevronRightTwoToneIcon />
                        </span>
                      </button>
                    </li>
                    <li
                      ref={menuSeven}
                      onKeyDown={(e) =>
                        handleSubmenuKeyDown(e, {
                          toggleRef: devToggleRef,
                          containerRef: menuSeven,
                          setOpen: setDevMenu,
                        })
                      }
                      style={{ maxHeight: devMenu ? `${devHeight}px` : "0px" }}
                    >
                      <button type="button"
                        tabIndex={devMenu ? 0 : -1}
                        onClick={handleSidebarToggle("/dev/controlTest")}
                        className={getOptionClass(
                          "/dev/controltest",
                          "groupSubOption"
                        )}
                      >
                        <span className="menuSubOptions">Control Test</span>
                      </button>

                      <button type="button"
                        tabIndex={devMenu ? 0 : -1}
                        onClick={handleSidebarToggle("/dev/tableTest")}
                        className={getOptionClass(
                          "/dev/tabletest",
                          "groupSubOption"
                        )}
                      >
                        <span className="menuSubOptions">Table Test</span>
                      </button>
                      <button type="button"
                        tabIndex={devMenu ? 0 : -1}
                        onClick={handleSidebarToggle("/dev/Token")}
                        className={getOptionClass(
                          "/dev/token",
                          "groupSubOption"
                        )}
                      >
                        <span className="menuSubOptions">Token</span>
                      </button>
                    </li>
                  </>
                )}
              </>
            )}
          </ul>
        )}
      </div>
    </>
  );
};

export default SidebarMenu;
